import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { HoldToConfirmButton } from "@src/appV2/redesign/components/HoldToConfirmButton";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInHours, parseISO } from "date-fns";
import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { convertModalShiftDataToUseBookShiftParams } from "../../Booking/convertModalShiftDataToUseBookShiftParams";
import { PreBookingCheck, useBookShift } from "../../Booking/useBookShift";
import { type ModalShiftData } from "../../types";
import { EducateNewWorkerDescription } from "./EducateNewWorkerDescription";
import { EducateNewWorkerTitle } from "./EducateNewWorkerTitle";
import { getCancellationPointsChange, getRestrictionInDays } from "./util";

interface EducateNewWorkerBottomSheetProps {
  readonly modalState: UseModalState;
  readonly shift: ModalShiftData;
  readonly rateNegotiationId: string | undefined;
}

export function EducateNewWorkerBottomSheet(props: EducateNewWorkerBottomSheetProps) {
  const { modalState, shift, rateNegotiationId } = props;

  const {
    id: shiftId,
    offer,
    attributes: { start: shiftStartIso },
  } = shift;

  const history = useHistory();
  const { navigateToModal, computedArrivalData } = useShiftModalsDataContext();

  const bookShiftParams = convertModalShiftDataToUseBookShiftParams({
    shift,
    navigateToModal,
    onBook: () => {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH, {
        pathParams: { shiftId },
      });
    },
    computedArrivalData,
    rateNegotiationId,
  });

  const {
    attemptBookingShift,
    isBooking,
    isLoading: isLoadingUseBookShift,
  } = useBookShift(bookShiftParams);

  const shiftLeadTimeInHours = differenceInHours(parseISO(shiftStartIso), new Date());

  const worker = useDefinedWorker();
  const { data: profile } = useGetClipboardScoreProfile(worker.userId);

  const cancellationPoints = getCancellationPointsChange(
    // If the shiftLeadTimeInHours is more than 24, we show the cancellation points for
    // the first 24 hour interval
    Math.min(shiftLeadTimeInHours, 24),
    profile?.policy
  );

  // Use restriction for score 0
  const restrictionInDays = getRestrictionInDays(0, profile?.policy);

  const hasRedirected = useRef(false);

  const hasAllData =
    isDefined(profile) &&
    isDefined(profile.policy) &&
    isDefined(cancellationPoints) &&
    isDefined(profile.clipboardScore) &&
    isDefined(restrictionInDays);

  useEffect(() => {
    if (hasRedirected.current) {
      return;
    }

    if (!hasAllData) {
      hasRedirected.current = true;

      navigateToModal(SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH, {
        pathParams: { shiftId },
      });
    }
  }, [hasAllData, navigateToModal, shiftId]);

  if (!hasAllData) {
    return null;
  }

  return (
    <BottomSheet
      modalState={modalState}
      header={
        <BottomSheetHeader
          onBack={() => {
            history.goBack();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical">
          <HoldToConfirmButton
            fullWidth
            isLoading={isLoadingUseBookShift || isBooking || !isDefined(offer)}
            endIconType="chevron-right"
            onConfirm={async () => {
              await attemptBookingShift({
                skipChecks: [PreBookingCheck.EDUCATE_NEW_WORKER],
              });
            }}
          >
            Press and hold to continue
          </HoldToConfirmButton>
        </DialogFooter>
      }
    >
      <DialogContent sx={{ backgroundColor: "unset" }}>
        <Stack direction="column" alignItems="center" spacing={5}>
          <EducateNewWorkerTitle />
          <EducateNewWorkerDescription
            shiftLeadTimeInHours={shiftLeadTimeInHours}
            cancellationPoints={cancellationPoints}
            currentClipboardScore={profile.clipboardScore}
            restrictionInDays={restrictionInDays}
          />
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
